/* eslint-disable max-len */
//Wrapper control used to allow <ep-field-inner> to be used without formGroupName divs and spans

import { Component, ElementRef, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import { FormGroupDirective, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DropdownValue } from '@Work/Accounts/proprietary/controls/dropdown.component';
import { FormValidationDirective } from '@Shared/Directives/proprietary/form-validation.directive';
import { AppService } from '@Core/services/proprietary/app.service';
import { VINType } from '@Core/services/proprietary/vin.service';

export interface FieldOptions {
    size?: number;
    rows?: number;
    codeValues?: [DropdownValue];
    nullValue?: DropdownValue;
    inputWidth?: string;
    showCode?: string;
    minDate?: string;
    maxDate?: string;
    popupOnFocus?: boolean;
    hideEmpty?: boolean;
    spellcheck?: boolean;
}

@Component({
    standalone: false,
    selector: 'ep-field',
    template: `
    <span
      *ngIf="!hidden && !(format == 'spacer' || format == 'container' || controlPath == null)"
      [formGroup]="_fieldFormGroup"
    >
      <ep-field-inner
        [formControlName]="_fieldName"
        [format]="format"
        [label]="label"
        [tooltip]="tooltip"
        [hideLabel]="hideLabel"
        [collapseLabelArea]="collapseLabelArea"
        [form]="_form"
        [columnSpan]="columnSpan"
        [formControlPath]="controlPath"
        [options]="options"
        [criteria]="criteria"
        [placeholder]="placeholder"
        [name]="name"
        [descFormControlPath]="descControlPath"
        (dataSelected)="dataSelected.emit($event)"
        (dataCleared)="dataCleared.emit($event)"
        (vinLookup)="vinLookup.emit($event)"
        [vinLookupData]="vinLookupData"
      >
      </ep-field-inner>
    </span>
    <span *ngIf="format == 'spacer' || format == 'container' || controlPath == null">
      <ep-field-inner
        [format]="format"
        [label]="label"
        [tooltip]="tooltip"
        [hideLabel]="hideLabel"
        [collapseLabelArea]="collapseLabelArea"
        [columnSpan]="columnSpan"
        [options]="options"
        [criteria]="criteria"
        [placeholder]="placeholder"
        [name]="name"
      >
        <ng-content></ng-content>
      </ep-field-inner>
    </span>
  `
})
export class FieldComponent implements OnInit {
    _fieldName;
    _fieldFormGroup: UntypedFormGroup;
    protected _descFieldName;
    protected _form: UntypedFormGroup;
    protected _formGroupPath: string;
    protected _nameOverride: string = null;

    @Input()
    label: string = null;

    @Input()
    tooltip: string = null;

    @Input()
    hideLabel: boolean = false;

    @Input()
    collapseLabelArea: boolean = false;

    @Input()
    columnSpan: string = '1';

    @Input()
    format: string = null;

    @Input()
    options: any = '{}';

    @Input()
    criteria: any = {}; //used by inline search

    @Input()
    placeholder: string = ''; //used by inline search

    @Input()
    controlPath: string = null;

    @Input()
    descControlPath: string = null;

    //  Normally the name property is not set on data fields (fields with a controlPath property).   The UX Designer uses the controlPath as the default name.
    //  Use to give spacer or containter type ep-fields a name.
    //  Also use to give a unique name to a field that is used multiple times on the same edit panel.   Only name the second instance  (e.g. name="Article.Loc_Num.2" )
    @Input()
    set name(value: string) {
        this._nameOverride = value;
    }

    get name(): string {
        if (this._nameOverride) {
            return this._nameOverride;
        }
        return this.controlPath;
    }

    @Input()
    formControlName: string = null; //obsolete parameter - for legacy error checking only

    @Input()
    form: any = null; //obsolete parameter - for legacy error checking only

    @Output()
    dataSelected = new EventEmitter(); //used by inline search

    @Output()
    dataCleared = new EventEmitter(); //used by inline search

    @Output()
    vinLookup = new EventEmitter(); //used for VIN Lookup

    @Input()
    vinLookupData: VINType = {}; //used for VIN Lookup

    formControl: UntypedFormControl;
    legacyMode: boolean = false;
    isFieldHidden: boolean = false;

    constructor(
        @Optional() private rootFormGroup: FormGroupDirective,
        @Optional() private formValidationDirective: FormValidationDirective,
        public element: ElementRef,
        protected app: AppService
    ) { }

    ngOnInit() {
        if (this.formControlName || this.form) {
            console.error(
                `Error: form and formControlName should not be used on <ep-field> version 2.  Remove 'form' and/or 'formControlName' attributes from the <ep-field> html element.  Use 'controlPath' instead`
            );
        }

        // set the form by getting form property of the injected FormControlDirective
        if (this.rootFormGroup) {
            this._form = this.rootFormGroup.form;
        }

        let lastSegPos = null;

        if (this.controlPath) {
            lastSegPos = this.controlPath.lastIndexOf('.');
            this._formGroupPath = this.controlPath.slice(0, lastSegPos); //path to the formgroup
            this._fieldName = this.controlPath.slice(lastSegPos + 1);
        }

        // if (this.descControlPath) {
        //   lastSegPos = this.descControlPath.lastIndexOf('.');
        //   this._descFieldName = this.descControlPath.slice(lastSegPos + 1);
        // }

        //find direct parent form group of this field
        if (this._formGroupPath) {
            let fg: UntypedFormGroup = this._form;
            let paths: string[] = this._formGroupPath.split('.');
            paths.forEach((pathSeg) => {
                fg = <UntypedFormGroup>fg.controls[pathSeg];
            });
            this._fieldFormGroup = fg;
        }
    }

    get hidden() {
        if (this.app.DesignerMode && this.app.DesignerModeShowHidden) {
            return false;
        }
        if (this.formValidationDirective) {
            let isHidden = this.formValidationDirective.hidden(this.controlPath);
            return isHidden;
        }

        return false;
    }
}
